import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

interface Webhook {
  id: number;
  url: string;
  events: string[];
  active: boolean;
  createdAt: string;
}

export default function WebhooksPage() {
  const qc = useQueryClient();
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [events, setEvents] = useState<string[]>(['audit.complete']);
  const [error, setError] = useState<string | null>(null);

  const { data: webhooks = [], isLoading } = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => apiClient.get<Webhook[]>('/webhooks').then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (body: { url: string; events: string[]; secret: string }) =>
      apiClient.post('/webhooks', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['webhooks'] });
      setUrl('');
      setSecret('');
      setError(null);
    },
    onError: () => setError('Failed to create webhook'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/webhooks/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url || !secret) { setError('URL and secret are required'); return; }
    createMutation.mutate({ url, events, secret });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Webhooks</h2>

      {/* Create form */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 max-w-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Add Webhook</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">HTTPS URL</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://your-endpoint.example.com/hook"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Signing Secret</label>
            <input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder="Min 16 characters"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Events</label>
            {(['audit.complete', 'audit.failed'] as const).map((ev) => (
              <label key={ev} className="flex items-center gap-2 text-sm text-gray-700 mb-1">
                <input
                  type="checkbox"
                  checked={events.includes(ev)}
                  onChange={(e) =>
                    setEvents(e.target.checked ? [...events, ev] : events.filter((x) => x !== ev))
                  }
                />
                {ev}
              </label>
            ))}
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg"
          >
            {createMutation.isPending ? 'Saving…' : 'Add Webhook'}
          </button>
        </form>
      </div>

      {/* Existing webhooks */}
      {!isLoading && webhooks.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase bg-gray-50">
                <th className="px-6 py-3">URL</th>
                <th className="px-6 py-3">Events</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {webhooks.map((wh) => (
                <tr key={wh.id} className="border-t border-gray-100">
                  <td className="px-6 py-3 text-gray-700 font-mono text-xs truncate max-w-xs">{wh.url}</td>
                  <td className="px-6 py-3 text-gray-500 text-xs">{wh.events.join(', ')}</td>
                  <td className="px-6 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${wh.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {wh.active ? 'active' : 'inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    <button
                      onClick={() => deleteMutation.mutate(wh.id)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
